import { useCallback, useMemo } from 'react'

import useUser from 'hooks/use-user'
import useUserPermissions from 'hooks/use-user-permissions'

import { LeadDuplicateDetails } from 'services/types/lead'
import { isLeadAccessible, LeadOwner, UserInfo } from 'utils/generic'
import { AccessType } from 'utils/constants'
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'

export const useLeadMergeCheck = () => {
  const [accountWorkflowPermissions] = useUserPermissions('accountWorkflow')
  const { canMerge, canEditLostInvalidLeads } = accountWorkflowPermissions
  const { currentUser } = useUser()
  const { teamOptions } = useWorkFlowLookup()

  const userAllTeamMembers: string[] = useMemo(() => {
    if (!currentUser?.teams) return []

    const usersTeamMembers = new Set(
      teamOptions?.filter((t) => currentUser?.teams.includes(t.value)).flatMap((item: any) => item?.teamMembers ?? []),
    )

    return Array.from(usersTeamMembers)
  }, [currentUser?.teams, teamOptions])

  const hasPermissionToMergeLead = useCallback(
    ({ leadData }: { leadData: LeadDuplicateDetails }) => {
      if (canMerge) {
        const isLostInvalid =
          leadData?.lead_status_name.toLowerCase() == 'invalid' || leadData?.lead_status_name.toLowerCase() == 'lost'

        const userInfo: UserInfo = {
          id: currentUser?.id,
          teamIds: currentUser?.teams,
        }
        const leadOwner: LeadOwner = {
          leadOwnerId: leadData?.owner_id || null,
          leadOwnerTeamId: leadData?.team_owner_id || null,
        }
        const collaborators = leadData?.additional_owner_ids || null

        if (isLostInvalid) {
          return isLostInvalid && canEditLostInvalidLeads
        }

        return isLeadAccessible(
          userInfo,
          leadOwner,
          collaborators,
          accountWorkflowPermissions,
          userAllTeamMembers || null,
          AccessType.edit,
          false,
        )
      } else {
        return false
      }
    },
    [
      canMerge,
      currentUser?.id,
      currentUser?.teams,
      accountWorkflowPermissions,
      userAllTeamMembers,
      canEditLostInvalidLeads,
    ],
  )

  return { hasPermissionToMergeLead }
}
